import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, MapPin, Plus, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { FieldErrors, useForm, useWatch } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { cn } from "src/utils";
import { StateList } from "src/utils/states";
import { DepartmentList } from "src/utils/DepartmentList";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/Command";
import { Employee } from "src/redux/types/_employee.type";
import { useDebounce } from "use-debounce";
import {
  EditEmployeeFormValues,
  EditEmployeeSchema,
} from "src/schemas/edit_employee";
import { ScrollArea } from "src/components/ui/ScrollArea";
import { useEmployees } from "./hook/useFetchEmployees";
import { useSearchEmployeeByCode } from "../Attendance/hooks/useSearchEmployeeByCode";
import {
  useAddDistrict,
  useAddState,
  useDeleteDistrict,
  useDeleteState,
  useEditEmployee,
} from "./hook/useEmployeeMutation";

interface CurrentStateItem {
  id: string;
  state: string;
}

interface CurrentDistrictItem {
  id: string;
  district: string;
}

interface SectionProps {
  className?: string;
  children: React.ReactNode;
  sectionTitle: string;
}

const Section = ({ className, children, sectionTitle }: SectionProps) => {
  return (
    <div
      className={cn(
        "rounded-lg shadow-md p-3 mb-4 flex flex-col gap-3 bg-card",
        className,
      )}
    >
      <h1 className="text-lg font-semibold text-gray-800">{sectionTitle}</h1>
      <div className="flex-1">{children}</div>
    </div>
  );
};

const stateDistrictMap: Record<string, string[]> = Object.fromEntries(
  StateList.map((item) => [item.state, item.districts]),
);

interface EditEmployeeProps {
  employee: Employee;
  back: () => void;
}

const EditEmployeeForm = ({ employee, back }: EditEmployeeProps) => {
  
  const { data: manager } = useSearchEmployeeByCode(
    employee.reporting_manager_code,
  );

  const form = useForm<EditEmployeeFormValues>({
    resolver: zodResolver(EditEmployeeSchema),
    defaultValues: {
      ...employee,
      date_of_joining: employee?.date_of_joining
        ? new Date(employee?.date_of_joining).toISOString().split("T")[0]
        : "",
      date_of_birth: employee?.date_of_birth
        ? new Date(employee?.date_of_birth).toISOString().split("T")[0]
        : "",
      employeement_type: employee.employeement_type,
      reporting_manager: undefined,
      stateDistricts: employee
        ? employee.state.map((s) => ({
            state: s.state,
            districts: employee.district.map((d) => d.district),
          }))
        : [],
    },
  });

  useEffect(() => {
    const managerId = manager?.[0]?.id;
    if (managerId) {
      form.reset({ ...form.getValues(), reporting_manager: managerId });
    }
  }, [manager, form]);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  const filter = {
    page: 1,
    pagesize: 10,
    name: debouncedSearch,
    employee_code: "",
  };

  const { data: employees = [] } = useEmployees(filter);

  const [selectedManagerName, setSelectedManagerName] = useState(
    employee.reporting_manager_name ?? "",
  );

  const onError = (errors: FieldErrors) => {
    console.log("Validation errors:", JSON.stringify(errors, null, 2));
  };

  const [currentStates, setCurrentStates] = useState<CurrentStateItem[]>(
    employee.state,
  );
  const [currentDistricts, setCurrentDistricts] = useState<
    CurrentDistrictItem[]
  >(employee.district);

  const isAddedState = (stateName: string) =>
    currentStates.some((s) => s.state === stateName);

  const isAddedDistrict = (district: string) =>
    currentDistricts.some((s) => s.district === district);

  const { mutateAsync: addStateMutation } = useAddState();
  const { mutateAsync: deleteStateMutation } = useDeleteState();

  const { mutateAsync: addDistrictMutation } = useAddDistrict();
  const { mutateAsync: deleteDistrictMutation } = useDeleteDistrict();

  // add state
  const handleAddState = async (state: string) => {
    addStateMutation({
      employeeId: employee.id,
      state,
    });
  };

  // delete state
  const handleDeleteState = async (id: string, state: string) => {
    deleteStateMutation(id, {
      onSuccess: () => {
        setCurrentStates((prev) => prev.filter((s) => s.state !== state));
      },
    });
  };

  // add district
  const handleAddDistrict = async (district: string) => {
    addDistrictMutation({
      employeeId: employee.id,
      district,
    });
  };

  // delete disctrict
  const handleDeleteDistrict = async (id: string, district: string) => {
    deleteDistrictMutation(id, {
      onSuccess: () => {
        setCurrentDistricts((prev) =>
          prev.filter((d) => d.district !== district),
        );
      },
    });
  };

  const employmentType = useWatch({
    control: form.control,
    name: "employeement_type",
  });

  const { mutateAsync: editEmployee, isPending } = useEditEmployee();

  const onSubmit = async (values: Partial<EditEmployeeFormValues>) => {
    editEmployee(
      { values: values, id: employee.id },
      {
        onSuccess: () => {
          back();
        },
      },
    );
  };

  const [searchDistrict, setSearchDistrict] = useState("");

  const originalEmploymentType = useRef(employee.employeement_type);

  useEffect(() => {
    const probationTypes = [
      "INTERN PROBATION",
      "EXTENDED INTERN PROBATION",
      "PROBATION",
      "EXTENDED PROBATION",
    ];

    const isPromotedToConfirmed =
      originalEmploymentType.current !== "CONFIRMED" &&
      probationTypes.includes(originalEmploymentType.current) &&
      employmentType === "CONFIRMED";

    if (isPromotedToConfirmed) {
      const previousEndDate = employee.end_date //form.getValues("end_date");
      const baseDate = previousEndDate ? new Date(previousEndDate) : new Date();

      // guard against an unparseable stored value
      const nextDay = Number.isNaN(baseDate.getTime())
        ? new Date()
        : new Date(baseDate);
      nextDay.setDate(nextDay.getDate() + 1);

      form.setValue("duration", "", {
        shouldDirty: true,
      });

      form.setValue("start_date", null, {
        shouldDirty: true,
      });

      form.setValue("end_date", nextDay.toISOString().split("T")[0], {
        shouldDirty: true,
      });
    }
  }, [employmentType, employee.employeement_type, form, employee.end_date]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onError)}>
          <Section sectionTitle="Personal Information">
            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto">
              <FormField
                control={form.control}
                name="full_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Full Name"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.full_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="1234567890"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.phone_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="example@gmail.com"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.email ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto my-3">
              <FormField
                control={form.control}
                name="date_of_birth"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date Of Birth</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        placeholder="MM-DD-YYYY"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.date_of_birth
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="marital_status"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marital Status</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Marital Status"
                            className={
                              form.formState.errors.marital_status
                                ? "border-red-400"
                                : ""
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {["Single", "Married", "Divorced", "Widowed"].map(
                            (status) => (
                              <SelectItem
                                key={status}
                                value={status}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {status}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Gender"
                            className={
                              form.formState.errors.gender
                                ? "border-red-500"
                                : ""
                            }
                          />
                          <SelectContent>
                            {["Male", "Female", "Other"].map((gender) => (
                              <SelectItem
                                key={gender}
                                value={gender}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {gender}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pin"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pin Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Employee Pin Code"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.pin ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nationality"
                defaultValue="Indian"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nationality</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Nationality"
                        {...field}
                        value="Indian"
                        readOnly
                        className={
                          form.formState.errors.nationality
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 w-[80%] mx-auto my-3 gap-4">
              <FormField
                control={form.control}
                name="permanent_address"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Permanent Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Complete Address"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.permanent_address
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="current_address"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Complete Address"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.current_address
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Employment Details">
            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto my-3">
              <FormField
                control={form.control}
                name="employeement_type"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employment Type</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Employment Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            "INTERN PROBATION",
                            "EXTENDED INTERN PROBATION",
                            "PROBATION",
                            "EXTENDED PROBATION",
                            "CONFIRMED",
                          ].map((type) => (
                            <SelectItem
                              key={type}
                              value={type}
                              className="focus:bg-accent focus:text-foreground"
                            >
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              {/* duration is only for probation */}
              {form.watch("employeement_type") !== "CONFIRMED" && (
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ""}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder="Select Duration"
                              className={
                                form.formState.errors.duration
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            <SelectContent>
                              {["6 Months", "9 Months", "12 Months"].map(
                                (d) => (
                                  <SelectItem
                                    key={d}
                                    value={d}
                                    className="focus:bg-accent focus:text-foreground"
                                  >
                                    {d}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </SelectTrigger>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Show Start Date only if NOT confirmed */}
                {form.watch("employeement_type") !== "CONFIRMED" && (
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* End Date / Confirmed Date */}
                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {form.watch("employeement_type") === "CONFIRMED"
                          ? "Confirmed Date"
                          : "End Date"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("designation", "");
                        }}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                        <SelectContent>
                          {DepartmentList.map((dept) => (
                            <SelectItem
                              key={dept.department}
                              value={dept.department}
                              className="focus:bg-accent focus:text-foreground"
                            >
                              {dept.department}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        Department is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="designation"
                render={({ field }) => {
                  const selectedDept = DepartmentList.find(
                    (dept) => dept.department === form.watch("department"),
                  );

                  return (
                    <FormItem>
                      <FormLabel>Designation</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedDept}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                selectedDept
                                  ? "Select Designation"
                                  : "Select Department first"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedDept?.designations.map((des) => (
                              <SelectItem
                                key={des}
                                value={des}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {des}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="text-red-400">
                          Designation is required
                        </FormDescription>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto my-3">
              {employee.designation !==
                "Mission Director / Managing Director (MD)" && (
                <FormField
                  control={form.control}
                  name="reporting_manager"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reporting Officer</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground",
                                form.formState.errors.reporting_manager &&
                                  "border-red-500",
                              )}
                            >
                              {field.value
                                ? selectedManagerName
                                : "Search & select manager..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>

                        <PopoverContent>
                          <Command>
                            <CommandInput
                              placeholder="Search employee..."
                              value={search}
                              onValueChange={setSearch}
                            />
                            <CommandList>
                              {employees.length === 0 && debouncedSearch && (
                                <CommandEmpty>No employees found.</CommandEmpty>
                              )}
                              <CommandGroup>
                                {employees.map((emp) => (
                                  <CommandItem
                                    key={emp.id}
                                    value={emp.full_name}
                                    onSelect={() => {
                                      form.setValue(
                                        "reporting_manager",
                                        emp.id,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      setSelectedManagerName(emp.full_name);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        emp.id === field.value
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                    {emp.full_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dashboard Role</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Role"
                            className={
                              form.formState.errors.role ? "border-red-500" : ""
                            }
                          />
                          <SelectContent>
                            {[
                              "MD",
                              "SO",
                              "ZWO",
                              "CHRWO",
                              "HRWO",
                              "CITWO",
                              "ITWO",
                              "FDEx",
                              "RWO",
                              "DWO",
                              "AWO",
                            ].map((role) => (
                              <SelectItem
                                key={role}
                                value={role}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto mt-4">
              <div className="flex flex-col gap-3 pt-2">
                <FormLabel>States</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between overflow-hidden"
                    >
                      {currentStates.length > 0
                        ? currentStates.map((s) => s.state).join(", ")
                        : "Select State"}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-80 p-3">
                    {/* CURRENT STATES */}
                    <h3 className="font-semibold mb-2 text-sm text-gray-600">
                      Current State(s)
                    </h3>

                    <ScrollArea className="max-h-32 border rounded-md mb-4 overflow-y-auto">
                      {currentStates.length > 0 ? (
                        currentStates.map((s) => (
                          <div
                            key={s.id}
                            className="flex justify-between items-center px-2 py-1 hover:bg-primary hover:text-white border-b last:border-none"
                          >
                            <span>{s.state}</span>

                            <button
                              className="p-2 hover:bg-red-200 rounded-xl"
                              onClick={() => handleDeleteState(s.id, s.state)}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 p-2">
                          No state selected.
                        </p>
                      )}
                    </ScrollArea>

                    {/* AVAILABLE STATE LIST */}
                    <h3 className="font-semibold mb-2 text-sm text-gray-600">
                      Add State
                    </h3>

                    <ScrollArea className="max-h-56 border rounded-md overflow-y-auto">
                      {StateList.map((item) => {
                        const alreadyAdded = isAddedState(item.state);

                        return (
                          <div
                            key={item.state}
                            className="flex justify-between items-center px-2 py-1 hover:bg-primary hover:text-white border-b last:border-none"
                          >
                            <span>{item.state}</span>

                            {!alreadyAdded ? (
                              <button
                                className="hover:bg-green-200 rounded-xl p-2"
                                onClick={() => handleAddState(item.state)}
                              >
                                <Plus className="w-4 h-4 text-green-600" />
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">
                                Added
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <FormLabel>Districts</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between overflow-hidden"
                    >
                      {currentDistricts.length > 0
                        ? currentDistricts.map((d) => d.district).join(", ")
                        : "Select District"}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-80 p-3">
                    {/* Current Districts */}
                    <h3 className="font-semibold mb-2 text-sm text-gray-600">
                      Current District(s)
                    </h3>

                    <ScrollArea className="max-h-40 border rounded-md mb-4 overflow-y-auto">
                      {currentDistricts.length > 0 ? (
                        currentDistricts.map((d) => (
                          <div
                            key={d.id}
                            className="flex justify-between items-center px-2 py-1 hover:bg-primary hover:text-white border-b last:border-none"
                          >
                            <span>{d.district}</span>
                            <button
                              className="p-2 hover:bg-red-200 rounded-xl"
                              onClick={() =>
                                handleDeleteDistrict(d.id, d.district)
                              }
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 p-2">
                          No district selected.
                        </p>
                      )}
                    </ScrollArea>

                    {/* Available Districts */}
                    <h3 className="font-semibold mb-2 text-sm text-gray-600">
                      Add District
                    </h3>

                    {currentStates.length === 0 ? (
                      // ── No states selected ──
                      <div className="flex flex-col items-center gap-1 py-4 text-center border rounded-md text-gray-400">
                        <MapPin className="w-4 h-4" />
                        <p className="text-xs">Please select a state first.</p>
                      </div>
                    ) : (
                      <>
                        <Input
                          placeholder="Search District..."
                          className="mb-2"
                          value={searchDistrict}
                          onChange={(e) => setSearchDistrict(e.target.value)}
                        />

                        <ScrollArea className="max-h-56 border rounded-md overflow-y-auto">
                          {currentStates.map((s) => {
                            // Get only this state's districts, filtered by search
                            const districtsForState = (
                              stateDistrictMap[s.state] ?? []
                            ).filter((d) =>
                              d
                                .toLowerCase()
                                .includes(searchDistrict.toLowerCase()),
                            );

                            return (
                              <div key={s.state}>
                                {/* State label header */}
                                <div className="px-2 py-1 bg-muted text-xs font-semibold text-muted-foreground sticky top-0 border-b">
                                  {s.state}
                                </div>

                                {districtsForState.length === 0 ? (
                                  <p className="text-xs text-gray-400 px-2 py-1 italic">
                                    No matching districts.
                                  </p>
                                ) : (
                                  districtsForState.map((d) => {
                                    const alreadyAdded = isAddedDistrict(d);
                                    return (
                                      <div
                                        key={d}
                                        className="flex justify-between items-center px-2 py-1 hover:bg-primary hover:text-white border-b last:border-none"
                                      >
                                        <span>{d}</span>
                                        {!alreadyAdded ? (
                                          <button
                                            className="p-2 hover:bg-green-200 rounded-xl"
                                            onClick={() => handleAddDistrict(d)}
                                          >
                                            <Plus className="w-4 h-4 text-green-600" />
                                          </button>
                                        ) : (
                                          <span className="text-xs text-gray-400">
                                            Added
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            );
                          })}
                        </ScrollArea>
                      </>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              <FormField
                control={form.control}
                name="date_of_joining"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Joining</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.date_of_joining
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Identification Details">
            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto my-3">
              <FormField
                control={form.control}
                name="adharcard_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aadhaar Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Aadhaar Number"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.adharcard_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pan_card_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PAN Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ABCDE1234F"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.pan_card_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passport_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passport Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Passport Number"
                        {...field}
                        className={
                          form.formState.errors.passport_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Banking Details">
            <div className="grid grid-cols-4 gap-4 w-[80%] mx-auto my-3">
              <FormField
                control={form.control}
                name="bank_ac_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bank Account Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Bank Account Number"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.bank_ac_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ifsc_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IFSC Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="SBIN0012345"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.ifsc_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bank Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="SBI / HDFC / ICICI"
                        {...field}
                        className={
                          form.formState.errors.bank_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="branch"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Branch</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Borpool / Jorhat"
                        {...field}
                        className={
                          form.formState.errors.branch ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[80%] mx-auto">
              <FormField
                control={form.control}
                name="pf_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PF Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter PF UAN"
                        {...field}
                        className={
                          form.formState.errors.pf_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="uan_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UAN Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter UAN"
                        {...field}
                        className={
                          form.formState.errors.uan_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="esic_uan"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ESIC UAN</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter ESIC UAN"
                        {...field}
                        className={
                          form.formState.errors.esic_uan ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>

          <Section sectionTitle="Emergency Contact Details">
            <div className="grid grid-cols-4 gap-4 w-[80%] mx-auto my-3">
              <FormField
                control={form.control}
                name="emergency_contact_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergency_contact_relationship"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Relationship</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Sister / Brother / Spouse"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_relationship
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergency_contact_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="9876543210"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergency_contact_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Emergency Contact Address"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Section>

          <div className="flex justify-center items-center p-4">
            <Button
              disabled={form.formState.isSubmitting || isPending}
              className="shadow-md hover:shadow-gray-600  bg-primary hover:bg-accent transition-all duration-300 ease-in-out"
            >
              {form.formState.isSubmitting || isPending ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditEmployeeForm;
